import { Link } from 'react-router-dom';
import { LegalShell, LegalSection as Section } from './LegalShell.jsx';

const CONTACT_EMAIL = 'distrx.io@gmail.com';
const LAST_UPDATED = '19 September 2026';

/**
 * Terms of Service for DistrxPay.
 *
 * Written to be accurate about what this platform actually is, because the
 * distinction carries real legal weight: DistrxPay *orchestrates* payments and
 * never holds, processes or settles funds. Saying otherwise — even loosely —
 * would describe a regulated activity we do not perform.
 */
export function TermsPage() {
  return (
    <LegalShell
      title="Terms of Service"
      updated={LAST_UPDATED}
      intro="These terms govern your use of DistrxPay. They are written to be read, not to be survived — if anything here is unclear, ask us before you agree to it."
    >
      <Section n="1" title="Who these terms are between">
        <p>
          These Terms of Service (“Terms”) are an agreement between you and distrx
          (“we”, “us”), the operator of DistrxPay. They govern your access to and use
          of the DistrxPay dashboard, API, point of sale, mobile applications and
          plugins (together, the “Service”).
        </p>
        <p>
          By creating an account or using the Service you accept these Terms and our{' '}
          <Link to="/privacy" className="legal-link">Privacy Policy</Link>. If you are
          acting for a business, you confirm you are authorised to bind it, and “you”
          means that business.
        </p>
      </Section>

      <Section n="2" title="What DistrxPay is — and is not">
        <p>
          DistrxPay is <strong>payment orchestration</strong>. It connects your business
          to payment providers you choose and have your own relationship with, hosts a
          checkout page carrying your branding, records what happened to each payment,
          and tells your systems about it.
        </p>
        <p>
          <strong>We are not a payment processor, acquirer, payment institution, money
          transmitter or bank.</strong> We do not hold, receive, control or settle your
          funds at any point. Money moves between your customer and your payment
          provider, or directly into a bank or UPI account you control. Any funds
          obligation is between you, your customer and your provider — not us.
        </p>
      </Section>

      <Section n="3" title="Your account and your team">
        <p>
          You are responsible for the accuracy of your account details, for keeping
          credentials confidential, and for everything done under your account. Roles
          exist so you can limit that: owners and admins can reach API keys, provider
          credentials and webhooks; staff and viewers cannot. Granting somebody a role
          is your decision and your responsibility.
        </p>
        <p>
          Tell us promptly at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>{' '}
          if you believe an account or a secret key has been compromised. You can revoke
          any API key yourself, at any time, from the dashboard.
        </p>
      </Section>

      <Section n="4" title="Payment providers are your relationship">
        <p>
          You connect your own provider accounts — such as Authorize.Net, Razorpay,
          Square, PayPal or CCAvenue — using credentials you obtain from them. Their
          terms, their fees, their settlement times, their compliance requirements and
          their decisions about your account are between you and them.
        </p>
        <p>
          We store the credentials you give us encrypted, use them only to act on your
          instructions, and delete them when you disconnect a provider. We cannot make a
          provider approve a payment, release a settlement or reverse a decision.
        </p>
      </Section>

      <Section n="5" title="Payments you collect yourself">
        <p>
          The Service also lets you take payments no provider is involved in — a bank
          transfer, a UPI transfer, a cheque or cash — by showing your own account
          details to the customer and letting you mark the payment received once the
          money arrives.
        </p>
        <p>
          When you use these, <strong>you</strong> are confirming a fact about your own
          bank account. We have no visibility of that money and no way to verify it. A
          payment you mark as received is recorded as received on your word, together
          with which member of your team said so and when. Marking a payment received
          that you have not actually received is a misuse of the Service.
        </p>
      </Section>

      <Section n="6" title="Card data and PCI">
        <p>
          Card details are captured and tokenized in your customer’s browser by your
          payment provider’s own code. Card numbers and security codes never reach our
          servers, and our API rejects a request containing them.
        </p>
        <p>
          That materially reduces your PCI DSS scope but does not eliminate your
          obligations. Determining and meeting the PCI requirements that apply to your
          business — including for any page of yours that loads a checkout — remains
          yours.
        </p>
      </Section>

      <Section n="7" title="Acceptable use">
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>take payments for anything unlawful where you or your customer are located, or anything prohibited by your payment provider’s own rules;</li>
          <li>test, validate or enumerate stolen card numbers, or otherwise probe a provider’s authorisation system;</li>
          <li>collect payments by deception, including misrepresenting who you are, what is being sold, or when it will be delivered;</li>
          <li>mark payments as received that you have not received, or otherwise falsify the record of a transaction;</li>
          <li>attempt to access data belonging to another business on the platform, or to disrupt, overload or reverse-engineer the Service.</li>
        </ul>
        <p>
          We may suspend an account that appears to be doing any of these, and will tell
          you why unless doing so would obstruct a fraud or legal investigation.
        </p>
      </Section>

      <Section n="8" title="Refunds, chargebacks and disputes">
        <p>
          Refunds you issue through the Service are instructions passed to your provider;
          whether and when they settle is theirs to determine. Chargebacks and disputes
          are handled entirely between you, your customer and your provider — we are not
          a party to them and cannot represent you in one.
        </p>
        <p>
          For payments you collected yourself, a refund is something you perform in your
          own bank; recording it here records what you did, it does not move money.
        </p>
      </Section>

      <Section n="9" title="Availability and support">
        <p>
          We work to keep the Service available and correct, but we do not promise it
          will be uninterrupted or error-free, and we do not offer a contractual uptime
          guarantee unless we have agreed one with you separately in writing.
        </p>
        <p>
          The Service includes a sandbox and a simulator so you can test an integration
          without moving real money. Please use them before you go live.
        </p>
      </Section>

      <Section n="10" title="Your data">
        <p>
          Your payment records, customers and settings belong to you. You can read them
          through the API and the dashboard at any time, and export them. How we handle
          personal data is set out in our{' '}
          <Link to="/privacy" className="legal-link">Privacy Policy</Link>.
        </p>
        <p>
          The activity stream — every payment, gateway response, retry, setting change
          and sign-in — is retained for 90 days by default and then deleted automatically.
        </p>
      </Section>

      <Section n="11" title="Fees">
        <p>
          Any fees for the Service itself are those we have agreed with you in writing.
          They are separate from, and additional to, whatever your payment provider
          charges you — we do not deduct anything from your transactions, because we are
          never in the flow of funds.
        </p>
      </Section>

      <Section n="12" title="Suspension and ending the agreement">
        <p>
          You may stop using the Service and close your account at any time. We may
          suspend or end access if you materially breach these Terms, if required by law
          or by a provider, or if your use creates a security risk to other businesses on
          the platform.
        </p>
        <p>
          After termination we retain transaction records for as long as we are required
          to, and delete the rest. Disconnecting a provider deletes its stored credentials
          immediately.
        </p>
      </Section>

      <Section n="13" title="Liability">
        <p>
          The Service is provided “as is” to the extent the law allows. We are not liable
          for indirect or consequential loss, for lost profits or revenue, or for loss
          caused by a payment provider’s acts, omissions, outages or decisions.
        </p>
        <p>
          Nothing in these Terms limits liability that cannot lawfully be limited —
          including for fraud, or for death or personal injury caused by negligence.
        </p>
      </Section>

      <Section n="14" title="Changes to these terms">
        <p>
          We may update these Terms as the product changes. Material changes will be
          notified to account owners by email before they take effect, and the date at
          the top of this page always reflects the current version. Continuing to use the
          Service after a change means you accept it.
        </p>
      </Section>

      <Section n="15" title="Contact">
        <p>
          Questions about these Terms go to{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>.
          We will tell you who we are and where we are registered on request, and in any
          agreement we sign with you.
        </p>
      </Section>
    </LegalShell>
  );
}

export default TermsPage;
